import { useCallback, useEffect, useRef } from 'react'
// queryKeys retained for other imports (no direct usage after extraction)
import { invalidatePreviousAnswerGroups } from './mutationSideEffects'
import { ACTIVITY_TYPES } from '../helpers/activity_types'
import { buildDeletePayload, applyLocalOptimisticClear } from '../helpers/clearAnswerUtils'
import { markAnswerAsRead } from '../services'
import useSaveQueue from './useSaveQueue'
import { ACTIONS as A } from '../components/activityGroupReducer/activityGroupReducer'

// Lightweight internal error logger (dev only)
const logError = (message, error) => {
  if (process?.env?.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(message, error)
  }
}

/**
 * Normalizes answer objects for saving to the backend.
 * - Accepts a single answer object or an array of answers.
 * - For answers of type 'selection', iterates over the 'selections' array:
 *   - If a selection does not have an 'id' and is not marked for deletion, omits the 'id' property from that selection.
 *   - Otherwise, retains the selection as-is.
 * - Returns a new array of normalized answer objects.
 *
 * @param {Object|Object[]} answerOrAnswers - An answer object or array of answer objects to normalize.
 * @returns {Object[]} Array of normalized answer objects ready for saving.
 */
const normalizeAnswersForSave = (answerOrAnswers) => {
  const answers = Array.isArray(answerOrAnswers) ? answerOrAnswers : [answerOrAnswers]
  if (!answers || answers.length === 0) return []

  return answers.map((answer) => {
    // Only special handling for selection answers
    if (answer?.type !== 'selection') return answer

    const normalizedSelections = (answer.selections || []).map((selection) => {
      if (!selection) return selection
      const { id: selectionId, delete: markedForDeletion } = selection

      // For unsaved (no id) selections that are NOT marked for deletion we omit the id prop
      if (!markedForDeletion && !selectionId) {
        const { id, ...selectionWithoutId } = selection // eslint-disable-line no-unused-vars
        return selectionWithoutId
      }
      return selection
    })

    return { ...answer, selections: normalizedSelections }
  })
}

/**
 * Encapsulates all ActivityGroup action logic (mutations, queue, optimistic updates)
 */
export default function useActivityGroupActions({
  state,
  dispatch,
  relevantActivityGroupId,
  activityGroupAnswerIdDerived,
  activityGroupAnswersDerived,
  queryClient,
  cisId,
  activeCisId,
  setCustomActivityGroupId,
  sharedAnswers,
  setSharedAnswers,
  saveAnswersMutation,
  markAsCompleteMutation,
  setGroupingMutation,
  deleteAnswerGroupMutation,
  isComponentMountedRef,
  routeHasExplicitId,
}) {
  // Keep latest answerGroupId for queued saves
  const currentAnswerGroupIdRef = useRef(activityGroupAnswerIdDerived)
  useEffect(() => {
    currentAnswerGroupIdRef.current = activityGroupAnswerIdDerived
  }, [activityGroupAnswerIdDerived])

  // Save queue serialization
  const { queueOperation, getQueueStatus, clearQueue } = useSaveQueue(
    async (operation) => {
      const {
        answersToSave,
        title,
        answerForPersonId,
        activityGroupId: activityGroupIdToUse,
        explicitActivityGroupAnswerId,
      } = operation || {}
      const activityGroupAnswerId = explicitActivityGroupAnswerId ?? currentAnswerGroupIdRef.current
      return saveAnswersMutation.mutateAsync({
        activityGroupId: activityGroupIdToUse,
        activityGroupAnswerId,
        answerForPersonId,
        data: { answers: answersToSave, title, answerForPersonId },
      })
    },
    (res, operation) => {
      const serverAnswers = res?.data?.answers
      if (serverAnswers?.length) {
        serverAnswers[0].type = operation?.answersToSave?.[0]?.type || serverAnswers[0].type
        dispatch({
          actionType: A.UPDATE_ANSWER,
          value: {
            answers: serverAnswers,
            title: res?.data?.title,
            activityGroupAnswerId: res?.data?.id,
            updateNav: false,
          },
        })
      }
    },
    (error) => {
      logError('Failed to save answer', error)
    }
  )

  const saveAnswer = useCallback(
    async ({
      answerOrAnswers,
      title,
      saveActivityGroupId,
      answerForPersonId,
      activityGroupAnswerId: explicitActivityGroupAnswerId,
    }) => {
      const activityGroupIdToUse = saveActivityGroupId || relevantActivityGroupId
      const answersToSave = normalizeAnswersForSave(answerOrAnswers)
      if (answersToSave.length < 1) return null
      dispatch({
        actionType: A.UPDATE_ANSWER,
        value: {
          answers: answersToSave,
          title,
          updateNav: true,
          baseActivityGroupAnswers: state.activityGroupAnswers ? null : activityGroupAnswersDerived,
        },
      })
      return queueOperation({
        answersToSave,
        title,
        answerForPersonId,
        activityGroupId: activityGroupIdToUse,
        explicitActivityGroupAnswerId,
      })
    },
    [relevantActivityGroupId, dispatch, queueOperation, state.activityGroupAnswers, activityGroupAnswersDerived]
  )

  const finishAnswers = useCallback(
    async (props) => {
      try {
        const agid = props?.activityGroupId ?? relevantActivityGroupId
        const ansId = props?.answerId ?? activityGroupAnswerIdDerived
        if (!agid || !ansId) return
        await markAsCompleteMutation.mutateAsync({ activityGroupId: agid, activityGroupAnswerId: ansId })
        invalidatePreviousAnswerGroups(queryClient, agid)
      } catch (e) {
        logError('finishAnswers failed', e)
      }
    },
    [markAsCompleteMutation, relevantActivityGroupId, activityGroupAnswerIdDerived, queryClient]
  )

  const deleteAnswers = useCallback(
    async ({ activityGroupId: agid, activityGroupAnswerId }) => {
      try {
        if (!agid || !activityGroupAnswerId) return
        await deleteAnswerGroupMutation.mutateAsync({ activityGroupId: agid, activityGroupAnswerId })
        invalidatePreviousAnswerGroups(queryClient, agid)
      } catch (e) {
        logError('deleteAnswers failed', e)
      }
    },
    [deleteAnswerGroupMutation, queryClient]
  )

  const setAnswerGrouping = useCallback(
    ({ grouping, saveActivityGroupId, activityGroupAnswerId }) =>
      setGroupingMutation.mutate({ activityGroupId: saveActivityGroupId, activityGroupAnswerId, grouping }),
    [setGroupingMutation]
  )

  const deleteAnswer = useCallback(
    (answer, title, saveActivityGroupId = relevantActivityGroupId) =>
      saveAnswersMutation.mutate({
        activityGroupId: saveActivityGroupId,
        activityGroupAnswerId: activityGroupAnswerIdDerived,
        data: { answers: [{ ...answer, delete: true }], title },
      }),
    [saveAnswersMutation, relevantActivityGroupId, activityGroupAnswerIdDerived]
  )

  const clearAnswer = useCallback(
    (activityId, type) => {
      const { answerToDelete, id, indexAGA } = buildDeletePayload({
        activityId,
        type,
        activityGroupAnswers: activityGroupAnswersDerived,
      })

      if (indexAGA > -1) {
        dispatch({
          actionType: A.CLEAR_ANSWER,
          value: {
            activityId,
            type,
            indexAGA,
            baseActivityGroupAnswers: state.activityGroupAnswers ? null : activityGroupAnswersDerived,
          },
        })
      } else if (type === ACTIVITY_TYPES.SELECT_SINGLE) {
        dispatch({
          actionType: A.CLEAR_ANSWER,
          value: {
            activityId,
            type,
            indexAGA: -1,
            baseActivityGroupAnswers: state.activityGroupAnswers ? null : activityGroupAnswersDerived,
          },
        })
      }

      applyLocalOptimisticClear({
        type,
        activityId,
        activityGroupAnswerId: activityGroupAnswerIdDerived,
        stateActivityGroupAnswers: state.activityGroupAnswers,
        cisId,
        requestedAnswerOwnerCisId: state?.requestedAnswerOwnerCisId,
        requestedActivityGroupId: state?.requestedActivityGroupId,
        activeCisId,
        relevantActivityGroupId,
        queryClient,
      })

      if (
        answerToDelete &&
        isComponentMountedRef.current &&
        (answerToDelete.type !== 'selection' || (answerToDelete.type === 'selection' && id))
      ) {
        deleteAnswer(answerToDelete)
      }
    },
    [
      activityGroupAnswersDerived,
      state.activityGroupAnswers,
      activityGroupAnswerIdDerived,
      cisId,
      state?.requestedAnswerOwnerCisId,
      state?.requestedActivityGroupId,
      activeCisId,
      relevantActivityGroupId,
      queryClient,
      dispatch,
      deleteAnswer,
      isComponentMountedRef,
    ]
  )

  const openPreviousAnswers = useCallback(
    ({ cisUserId, activityGroupId, activityGroupAnswerId, isUnread, activityGroupMode }) => {
      if (activityGroupMode) {
        dispatch({ actionType: A.SET_ACTIVITYGROUP_MODE, activityGroupMode })
      }
      dispatch({ actionType: A.SET_IS_ACTIVITY_GROUP_OPEN, value: { isActivityGroupOpen: true, activityGroupId } })
      if (activityGroupId && !relevantActivityGroupId) setCustomActivityGroupId(activityGroupId)
      dispatch({
        actionType: A.UPDATE_ACTIVITY_GROUP_ANSWER_ID,
        value: { activityGroupAnswerId, ownerCisId: cisUserId },
      })
      if (isUnread) {
        markAnswerAsRead(activityGroupAnswerId)
        const allAnswersCopy = sharedAnswers ? [...sharedAnswers] : []
        setSharedAnswers(allAnswersCopy)
      }
    },
    [relevantActivityGroupId, setCustomActivityGroupId, sharedAnswers, setSharedAnswers, dispatch]
  )

  // When the activity group is closed, clear the requestedActivityGroupId so a new feed open can set a fresh one.
  useEffect(() => {
    // Only clear requested/custom ids after close when we are NOT on a route with an explicit id.
    // If a route id exists, letting it persist avoids a sync loop (route -> requested -> close -> clear -> sync -> ...)
    if (!state.isActivityGroupOpen && !routeHasExplicitId) {
      if (state.requestedActivityGroupId) {
        // Simple setter: supply property directly for handleReducerSimpleSetter
        dispatch({
          actionType: A.SET_REQUESTED_ACTIVITY_GROUP_ID,
          requestedActivityGroupId: null,
        })
      }
      if (setCustomActivityGroupId) setCustomActivityGroupId(null)
    }
  }, [
    state.isActivityGroupOpen,
    state.requestedActivityGroupId,
    routeHasExplicitId,
    dispatch,
    setCustomActivityGroupId,
  ])

  return {
    saveAnswer,
    finishAnswers,
    openPreviousAnswers,
    clearAnswer,
    deleteAnswer,
    deleteAnswers,
    setAnswerGrouping,
    saveQueueStatus: getQueueStatus(),
    saveQueueBusy: saveAnswersMutation.isPending,
    clearSaveQueue: clearQueue,
  }
}
